import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { DataNodeData, WorkflowNodeData } from '../types'
import { themeColor } from '../../../layout/theme/color'
import ExpressionTextField from '../autocomplete/ExpressionTextField'

interface DataConfigProps {
  data: DataNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const DataConfig: FC<DataConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()
  const attachmentRole = (data.attachmentRole ?? '').trim()
  const attachmentLabel = data.attachmentLabel ?? ''
  const attachmentDescription = data.attachmentDescription ?? ''
  const attachmentFormatHint = data.attachmentFormatHint ?? ''

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!attachmentRole && (
        <Alert severity="warning">
          {translate('workflow.config.attachmentRoleRequired')}
        </Alert>
      )}

      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      <TextField
        required
        error={!attachmentRole}
        label={translate('workflow.config.attachmentRole')}
        value={attachmentRole}
        onChange={(e) => onUpdate({ attachmentRole: e.target.value })}
        size="small"
        fullWidth
        placeholder="source_xml"
        helperText={translate('workflow.config.attachmentRoleHelp')}
        InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
      />

      <TextField
        label={translate('workflow.config.attachmentLabel')}
        value={attachmentLabel}
        onChange={(e) => onUpdate({ attachmentLabel: e.target.value })}
        size="small"
        fullWidth
        helperText={translate('workflow.config.attachmentLabelHelp')}
      />

      <TextField
        label={translate('workflow.config.attachmentDescription')}
        value={attachmentDescription}
        onChange={(e) => onUpdate({ attachmentDescription: e.target.value })}
        size="small"
        fullWidth
        multiline
        minRows={3}
        helperText={translate('workflow.config.attachmentDescriptionHelp')}
      />

      <TextField
        label={translate('workflow.config.attachmentFormatHint')}
        value={attachmentFormatHint}
        onChange={(e) => onUpdate({ attachmentFormatHint: e.target.value })}
        size="small"
        fullWidth
        helperText={translate('workflow.config.attachmentFormatHintHelp')}
        placeholder="application/xml"
      />

      <TextField
        select
        label={translate('workflow.config.format')}
        value={data.format}
        onChange={(e) => onUpdate({ format: e.target.value as 'xml' | 'json' | 'csv' })}
        size="small"
        fullWidth
      >
        <MenuItem value="xml">XML</MenuItem>
        <MenuItem value="json">JSON</MenuItem>
        <MenuItem value="csv">CSV</MenuItem>
      </TextField>

      {data.format === 'csv' ? (
        <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
          {translate('workflow.config.csvHelp')}
        </Alert>
      ) : (
        <Box>
          <ExpressionTextField
            label={translate('workflow.config.selector')}
            value={data.selector}
            onChange={(e) => onUpdate({ selector: e.target.value })}
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder={
              data.format === 'xml'
                ? './processen/proces/velden/kernomschrijving[.=...]/../..'
                : 'data.0.field'
            }
            InputProps={{ sx: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: 12 } }}
          />
          <Typography variant="caption" sx={{ color: themeColor.slate60, mt: 0.5, display: 'block' }}>
            {translate('workflow.config.selectorHelp')}
          </Typography>
        </Box>
      )}

    </Box>
  )
}

export default DataConfig
